import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useBack } from '../../lib/back.js';
import { theme } from '../../theme.js';
import { Icon } from '../../components/Icon.js';
import { PageShell } from '../../components/PageShell.js';
import { useAccess } from '../../context/AuthContext.js';

// leaderToo: also shown to a team's leader or deputy (who only manage their team).
const ADMIN_TABS = [
  { to: '/admin/assets',    label: 'Assets',    icon: 'truck'    },
  { to: '/admin/equipment', label: 'Equipment', icon: 'package'  },
  { to: '/admin/locations', label: 'Locations', icon: 'pin'      },
  { to: '/admin/roster',    label: 'Roster',    icon: 'calendar' },
  { to: '/admin/teams',     label: 'Teams',     icon: 'people',   leaderToo: true },
  { to: '/admin/members',   label: 'Members',   icon: 'shield'   },
  { to: '/admin/quals',     label: 'Quals & positions', icon: 'list' },
  { to: '/admin/schedules', label: 'Schedules', icon: 'calendar' },
  { to: '/admin/activity',  label: 'Activity',  icon: 'clock'    },
  { to: '/admin/reports',   label: 'Reports',   icon: 'doc'      },
] as const;

export function AdminLayout() {
  const nav = useNavigate();
  const { isAdmin } = useAccess();
  const tabs = ADMIN_TABS.filter((t) => isAdmin || 'leaderToo' in t);
  // On a tab, Back leaves Admin (replacing, so the phone's back doesn't
  // return here). Inside one (an asset), it goes up to that tab's list.
  const [, , tab, detail] = useLocation().pathname.split('/');
  const backToList = useBack(`/admin/${tab}`);
  const onBack = detail ? backToList : () => nav('/', { replace: true });

  return (
    <PageShell wide>
      {/* admin header */}
      <div style={{
        padding: '14px 18px 0',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={onBack} style={iconBtn} aria-label="Back">
            <Icon name="back" size={18} />
          </button>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, letterSpacing: -0.5 }}>
            Admin
          </div>
        </div>

        {/* tab row */}
        <div className="tab-row">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                borderBottom: isActive ? `2px solid ${theme.accent}` : '2px solid transparent',
                color: isActive ? theme.accent : theme.textDim,
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
                whiteSpace: 'nowrap', fontFamily: theme.font,
                transition: 'color .1s',
              })}
            >
              <Icon name={tab.icon} size={15} />
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* routed content */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
    </PageShell>
  );
}

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`,
  background: theme.surface2, color: theme.text, borderRadius: 10,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
